<template lang="pug">
    v-dialog(v-model='dialog' persistent max-width='800px')
        template(v-slot:activator='{ on }')
            v-btn(
                icon
                theme="light"
                :color="validAppointment ? 'white' : 'primary'"
                v-on='on'
            )
                v-icon {{ currentIcon }}

        template(v-if="validAppointment")
            UpdateAppointmentForm(
                :appointment="appointment"
                :appointment-scheduling-hour="currentAppointmentSchedulingHour"
                :close-dialog="closeDialog"
                :hide-agent-selection="true"
                :show-snackbar="showSnackbar"
                @appointment-updated="refreshAppointments"
            )

        template(v-else)
            v-card
                v-toolbar.volie-v-toolbar-color(dark)
                    v-toolbar-title Schedule Appointment
                v-card-text.pa-6
                    AppointmentForm(
                        :appointment-scheduling-hour="currentAppointmentSchedulingHour"
                        :customer-profile-appointment="true"
                        :customer="customer"
                        :on-appointment-change="onAppointmentChange"
                        :set-appointment-is-unique-if-required="() => {}"
                        :show-vehicles-dropdown="true"
                        :time-zone-id="customerTimeZoneId"
                        :use-on-change="true"
                        :valid-customer="validCustomer"
                    )

                v-card-actions.pa-2
                    v-spacer
                    v-btn(
                        color="primary"
                        outlined
                        @click="dialog = false"
                    )
                        v-icon mdi-window-close
                        | Cancel

                    v-btn(
                        color="success"
                        :disabled="!isEligible"
                        :loading="isLoading"
                        @click="handleCreateAppointment"
                    )
                        v-icon mdi-calendar-check
                        | Schedule Appointment

</template>

<script lang="ts">
import Vue from 'vue';

// Components
import AppointmentForm from '@/components/appointments/form.vue';
import UpdateAppointmentForm from '@/components/dashboards/appointment/form.vue';

// Data
import { CreateAppointment, GetAppointmentSchedulingHours } from "@/data";

// Helpers
import { jsDayToFriendly } from "@/helpers/general";

export default Vue.extend({
    data() {
        return {
            formAppointment: null,
            appointmentSchedulingHour: null,
            dialog: false,
            isEligible: false,
            isLoading: false,
        }
    },
    methods: {
        closeDialog() {
            this.dialog = false;
        },
        getAppointmentSchedulingHours() {
            if (!this.validOrganization) {
                return;
            }

            const options = {
                ownable_id: this.customer?.organization_id,
                ownable_type: "organization"
            }

            GetAppointmentSchedulingHours(options).then((response) => {
                if (response?.Data?.appointment_scheduling_hours?.length === 1) {
                    this.appointmentSchedulingHour = response.Data.appointment_scheduling_hours[0];
                }
            });
        },
        handleCreateAppointment() {
            // Validate eligibility
            if (!this.isEligible) {
                return;
            }

            const options = {
                account_id: this.customer?.account_id || 0,
                appointment_datetime_date: this.formAppointment?.appointment_datetime_date || '',
                appointment_datetime_time: this.formAppointment?.appointment_datetime_time || '',
                appointment_number: this.formAppointment?.appointment_number || null,
                appointment_type_id: this.formAppointment?.appointment_type_id || 0,
                campaign_customer_id: this.customer?.campaign_customer_id || null,
                campaign_id: this.customer?.campaign_id || null,
                customer_id: this.customer?.customer_id || 0,
                customer_phone_number: null,
                customer_vehicle_id: this.formAppointment?.customer_vehicle_id || null,
                organization_id: this.customer?.organization_id || 0,
                source: this.formAppointment?.source || null,
                special_instructions: this.formAppointment?.special_instructions || null,
                time_zone_id: this.formAppointment?.time_zone_id || 0,
                user_id: this.formAppointment?.user_id || this.currentUser?.id || 0, // If the form doesn't have a user_id (field is hidden), enforce the current user instead
                vehicle_id: this.formAppointment?.vehicle_id || null,
            }

            // Check all customer phone numbers before setting the customer_phone_number field
            if (this.customer?.home_phone?.length > 0) {
                options.customer_phone_number = this.customer.home_phone;
            } else if (this.customer?.cell_phone?.length > 0) {
                options.customer_phone_number = this.customer.cell_phone;
            } else if (this.customer?.work_phone?.length > 0) {
                options.customer_phone_number = this.customer.work_phone;
            } else if (this.customer?.phone_numbers?.length > 0) {
                options.customer_phone_number = this.customer.phone_numbers[0];
            } else {
                options.customer_phone_number = null;
            }

            this.isLoading = true;
            CreateAppointment(options).then((response) => {
                if (response?.Data?.appointment?.appointment_id > 0) {
                    this.showSnackbar("success", "Successfully created appointment");
                    if (this.refreshAppointments !== undefined && this.refreshAppointments !== null) {
                        this.refreshAppointments();
                    }

                    this.dialog = false;
                } else {

                    let err = "Unable to create appointment"
                    if (response?.Error?.length > 0) {
                        err = response.Error
                    }
                    this.showSnackbar("error", err);
                }

                this.isLoading = false;
            });
        },
        handleKeyDown(event) {
            if (event.key === 'Escape') {
                this.dialog = false;
            }
        },
        initForm() {
            if(this.appointment?.appointment_id > 0) {
                this.formAppointment = {...this.appointment};
            }
        },
        onAppointmentChange(appointment) {
            if (appointment) {
                this.formAppointment = appointment;
            }

            this.validateAppointment();
        },
        validateAppointment() {
            this.isEligible = !!(
                this.formAppointment &&
                this.formAppointment.appointment_datetime_date &&
                this.formAppointment.appointment_datetime_time &&
                this.formAppointment.time_zone_id
            );
        },
    },
    computed: {
        currentAppointmentSchedulingHour() {
            if (!(this.appointmentSchedulingHour?.days?.length > 0)) {
                return null;
            }

            // For existing appointments, use the appointment date
            // For new appointments, use the form date
            let appointmentDate;
            if (this.validAppointment) {
                appointmentDate = this.appointment?.appointment_datetime_date || this.appointment?.appointment_datetime;
            } else {
                appointmentDate = this.formAppointment?.appointment_datetime_date;
            }

            if (!appointmentDate || appointmentDate === "") {
                return null;
            }

            // Init current time in local
            const localNow = new Date(appointmentDate);

            // Convert to utc so it matches date selected
            const current = new Date(localNow.getTime() + localNow.getTimezoneOffset() * 60000);

            // Loop over and find matching day
            for (let i = 0; i < this.appointmentSchedulingHour.days.length; i++) {
                // Init day
                const day = this.appointmentSchedulingHour.days[i];
                if (day?.day !== jsDayToFriendly(current.getDay())) {
                    continue;
                }
                return day;
            }

            return null;
        },
        customerTimeZoneId() {

            // Use customer's time zone
            if (this.customer?.customer?.time_zone_id > 0) {
                return this.customer.customer.time_zone_id;
            }

            // Fallback to organization's time zone
            if (this.customer?.organization_time_zone_id > 0) {
                return this.customer?.organization_time_zone_id;
            }

            return -1;
        },
        currentIcon() {
            if (this.validAppointment) {
                return "mdi-calendar-edit-outline";
            }

            return "mdi-calendar-plus-outline";
        },
        validAppointment() {
            return this.appointment?.appointment_id > 0;
        },
        validOrganization() {
            return this.customer?.organization_id > 0;
        }
    },
    components: {
        AppointmentForm,
        UpdateAppointmentForm,
    },
    props: {
        appointment: Object,
        customer: {
            type: Object,
            required: true,
        },
        refreshAppointments: Function,
        showSnackbar: Function,
        validCustomer: {
            type: Boolean,
            required: true,
        },
    },
    watch: {

    },
    created() {
        this.getAppointmentSchedulingHours();
        this.initForm();
        window.addEventListener('keydown', this.handleKeyDown);
    },
    destroyed() {
        window.removeEventListener('keydown', this.handleKeyDown);
    },
    mounted() {
    },
})
</script>

<style lang="scss" scoped>

</style>
